<template>
  <div
    :id="props.scrollable ? 'scrollable' : ''"
    ref="scrollableRef"
    class="flex flex-col items-center md:flex-row flex-wrap md:flex-nowrap gap-8 w-full md:max-w-full mx-auto min-h-full md:min-h-fit relative"
  >
    <div class=" absolute top-[-10px] md:left-0 w-full mx-auto md:mx-0 max-w-[320px] md:max-w-full">
      <button
        v-if="!(props.disablePast && currentMonth.date.isBefore(ExtendedDate.today().getFirstDayOfMonth(), true))"
        :aria-label="t('prev_month')"
        class="aspect-square bg-white rounded-full drop-shadow-md text-center border border-neutral-100 p-2 h-[40px] leading-0 touch-manipulation float-left"
        data-datepicker
        @click="currentMonth.prev()"
      >
        <UiIcon src="/svg/daterangepicker/arrow.svg" width="19" height="13" alt="vor" class="rotate-90 inline-block" />
      </button>
      <button
        :aria-label="t('next_month')"
        :class="[{'max-md:hidden' : navigationVerticalMobil}, 'aspect-square bg-white rounded-full drop-shadow-md text-center border border-neutral-100 p-2 h-[40px] leading-0 touch-manipulation float-right']"
        data-datepicker
        @click="currentMonth.next()"
      >
        <UiIcon src="/svg/daterangepicker/arrow.svg" width="19" height="13" alt="vor" class="rotate-270 inline-block" />
      </button>
    </div>
    <div v-for="month in monthsList" :key="`${month.year}-${month.month}`" class="w-full max-w-[320px] h-auto md:basis-1/2">
      <div class="text-center text-xl font-semibold mb-3">
        {{ t(`monthname_${month.month}`) }}
        {{ month.year }}
      </div>
      <div class="grid grid-cols-7 gap-y-1 text-center text-sm py-2">
        <span v-for="i in 7" :key="i">
          {{ t(`dayname_${i}`) }}
        </span>
      </div>
      <div class="grid grid-cols-7 gap-y-1 text-center">
        <span v-for="day in month.daysBefore" :key="day.dmy" class="day dummy">
          {{ day.getDate() }}
        </span>
        <span
          v-for="day in month.days"
          :key="day.dmy"
          :class="dayClasses(day)"
          data-day
          :aria-label="getAria(day)"
          v-bind="!dayClasses(day).disabled ? { role: 'button', tabindex: '0' } : {}"
          @click="onClickDay(day)"
          @keydown.enter="onClickDay(day)"
          @mouseenter="onMouseEnterDay(day)"
          @mouseleave="onMouseLeaveDay(day)"
        >
          {{ day.getDate() }}
          <div v-if="hasTooltip(day) && props.tooltipActive && tooltipText(day) !== 0" class="tooltip" :style="tooltipClasses(day)">
            {{ tooltipText(day) }} {{ t(`nights.${numerus(tooltipText(day))}`, { nights: tooltipText(day) }) }}
          </div>
        </span>
      </div>
    </div>
  </div>
  <div :class="[{'md:hidden' : navigationVerticalMobil}, {'hidden' : navigationHorizontalMobil}, 'min-h-[10px]']">
    <button
      :aria-label="t('next_month')"
      :class="[{'hidden' : currentMonth.maxMonths <= 11}, 'aspect-square bg-white rounded-full drop-shadow-md text-center border border-neutral-100 p-2 h-[40px] leading-0 touch-manipulation']"
      @click="currentMonth.next()"
    >
      <UiIcon src="/svg/daterangepicker/arrow.svg" width="19" height="13" alt="vor" class="inline-block" />
    </button>
  </div>
  <div v-if="!chosenDates.isEmpty() && resetButton" :class="['grid place-content-end w-full md:w-auto md:p-0', {'fixed md:static bottom-0 left-0 border-t-2 border-neutral-100 md:border-0 bg-white py-2 px-5': resetPositionFixed}]">
    <button
      type="button"
      class="btn-reset p-0 underline font-semibold cursor-pointer"
      @click="chosenDates.reset(true)"
    >
      <UiLanguagePlaceholder domain="UiDateRangePicker" name="reset" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { makeCalendar } from '~/utils/Calendar'

interface Props {
  from?: string
  to?: string
  dateRangeCheck?: (from: ExtendedDate, to?: ExtendedDate) => boolean
  disablePast?: boolean
  months?: number
  buttonActive?: boolean
  navigationType?: 'vertical' | 'horizontal'
  resetButton?: boolean
  datesResetPosition?: string
  tooltipActive?: boolean
  isFlex?: boolean
  scrollable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  from: '',
  to: '',
  dateRangeCheck: () => true,
  disablePast: true,
  months: 2,
  buttonActive: false,
  navigationType: 'vertical',
  resetButton: true,
  datesResetPosition: 'fixed',
  tooltipActive: false,
  isFlex: false,
  scrollable: false
})

const { translate: t, numerus } = useTranslations('UiDateRangePicker')
const { currentPortal } = usePortals()
const onClickDay = (day: ExtendedDate) => {
  if (chosenDates.isOnlyFirstDateChosen() && day.equals(chosenDates.from)) {
    chosenDates.reset(true)
    return
  }
  chooseDate(day)
  chosenDates.tooltip = day
  if ('ontouchstart' in window) {
    setTimeout(() => {
      if (chosenDates.tooltip === day) {
        chosenDates.tooltip = null
      }
    }, 2000)
  }
}
const onMouseEnterDay = (day: ExtendedDate) => {
  previewDate(day)
  startTooltipTimer(day)
}

const onMouseLeaveDay = (_day: ExtendedDate) => {
  clearTooltipTimer()
  isHovering.value = false
}

const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement | null
  if (target?.closest('[data-datepicker]')) {
    return
  }

  if (chosenDates.isOnlyFirstDateChosen()) {
    chosenDates.reset(true)
  }
  clearTooltipTimer()

  emit('clickOutside')
}

const previewDate = (day: ExtendedDate) => {
  if (!chosenDates.isOnlyFirstDateChosen()) { return }
  chosenDates.toPreview = day
}

const dayClasses = computed(() => (day: ExtendedDate) => {
  const end = chosenDates.to || chosenDates.toPreview
  const previewBeforeStart = chosenDates.isOnlyFirstDateChosen() && chosenDates.toPreview && chosenDates.toPreview.isBefore(chosenDates.from)
  const hasRange = chosenDates.from && end && !previewBeforeStart
  const lower = hasRange && end.isBefore(chosenDates.from) ? end : chosenDates.from
  const upper = hasRange && end.isBefore(chosenDates.from) ? chosenDates.from : end

  return {
    day: true,
    'start-range': day.equals(chosenDates.from),
    'end-range': previewBeforeStart ? false : (end ? day.equals(end) : false),
    'in-range': hasRange ? day.isBetween(lower, upper, false) : false,
    today: day.equals(ExtendedDate.today()),
    disabled: isDayDisabled.value(day),
    'disabled-to': isDayDisabledTo.value(day)
  }
})

const getAria = computed(() => {
  return (day: ExtendedDate) => {
    if (!isDayDisabled.value(day)) {
      return day.getAria(currentPortal.value.portal.lang)
    }
  }
})

const isDayDisabled = computed(() => {
  const today = ExtendedDate.today()
  const blockPast = props.disablePast !== false // default: block past, allow future
  const blockFuture = !blockPast // disablePast === false → block future

  return (day: ExtendedDate) => {
    // Never disable today or the already chosen start date
    if (day.equals(today) || day.equals(chosenDates.from)) { return false }

    // Block past or future depending on prop
    if (blockPast && day.isBefore(today, true)) { return true }
    if (blockFuture && day.isAfter(today, true)) { return true }

    // If only start is chosen, allow days before start to swap start/end
    if (chosenDates.isOnlyFirstDateChosen()) {
      // Allow dates before the current start so they can become the new start
      if (day.isBefore(chosenDates.from)) {
        return false
      }
      // For dates after the start, apply the range check
      return !props.dateRangeCheck(chosenDates.from, day)
    }

    // With both start and end selected, apply normal check
    return !props.dateRangeCheck(day)
  }
})

const isDayDisabledTo = computed(() => _day => false)

const tooltipClasses = (_day) => {
  return { 'margin-top': '-32px' }
}

const tooltipText = computed(() => (day: ExtendedDate) => {
  if (chosenDates.from && !chosenDates.to) {
    const diff = Math.round((new Date(day) - new Date(chosenDates.from)) / (1000 * 60 * 60 * 24))
    return Math.abs(diff) // Absolute value to avoid negative numbers
  }
  return ''
})

const isHovering = ref(false)

const hasTooltip = computed(() => {
  return (day: ExtendedDate) => {
    if (chosenDates.from && !chosenDates.to) {
      if (day.isBefore(chosenDates.from)) { return false }
      return chosenDates.tooltip === day && (isHovering.value || isAvailableSomehow.value(day))
    }
    return false
  }
})
interface CurrentMonth {
  date: ExtendedDate
  maxMonths: number
  shownDays: {
    first: ExtendedDate
    last: ExtendedDate
  }
  setDate(date: ExtendedDate): void
  next(): void
  addNext(): void
  prev(): void
  adapt(from: ExtendedDate | null, to: ExtendedDate | null): void
}
const currentMonth = reactive({
  date: props.from ? new ExtendedDate(props.from).getFirstDayOfMonth() : ExtendedDate.today().getFirstDayOfMonth(),
  maxMonths: props.months,
  get shownDays () {
    return {
      first: this.date,
      last: this.date.addMonths(this.maxMonths).addDays(-1)
    }
  },
  setDate (date) {
    this.date = date.getFirstDayOfMonth()
  },
  next () {
    this.date = this.date.addMonths(1)
  },
  addNext () {
    this.maxMonths++
  },
  prev () {
    this.date = this.date.addMonths(-1)
  },
  adapt (from, to) {
    if (!props.isFlex && from?.isValid() && to?.isValid() && !(from.isBetween(this.shownDays.first, this.shownDays.last) && to.isBetween(this.shownDays.first, this.shownDays.last))) {
      this.setDate(from)
    }
  }
}) as CurrentMonth

const scrollableRef = useTemplateRef('scrollableRef')
const handleScroll = (): void => {
  const element = scrollableRef.value?.parentElement
  const scrollable = scrollableRef.value
  if (!scrollable || !element) {
    return
  }
  const cs = getComputedStyle(element)
  const end = element.scrollTop + element.offsetHeight - scrollable.offsetHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom)

  if (end > 0 && currentMonth.maxMonths < 12) {
    currentMonth.addNext()
  }
}
const checkHeight = (): void => {
  const element = scrollableRef.value?.parentElement
  const scrollable = scrollableRef.value
  if (!scrollable || !element) {
    return
  }

  if (element.offsetHeight > scrollable.offsetHeight) {
    currentMonth.addNext()
  }
}

let infinite: HTMLElement | null | undefined = null

onMounted(() => {
  if (props.scrollable) {
    infinite = scrollableRef.value?.parentElement
    if (!infinite) {
      return
    }
    checkHeight()
    infinite.addEventListener('scroll', handleScroll)
  }
  document.addEventListener('click', handleOutsideClick)
  document.addEventListener('touchstart', handleOutsideClick, { passive: true })
})

onUnmounted(() => {
  if (props.scrollable && infinite) {
    infinite.removeEventListener('scroll', handleScroll)
  }
  document.removeEventListener('click', handleOutsideClick)
  document.removeEventListener('touchstart', handleOutsideClick)
})

const monthsList = computed(() => makeCalendar(
  currentMonth.date,
  currentMonth.maxMonths
))

const navigationVerticalMobil = computed(() => props.navigationType === 'vertical')
const navigationHorizontalMobil = computed(() => props.navigationType === 'horizontal')
const resetPositionFixed = computed(() => props.datesResetPosition === 'fixed')

const emit = defineEmits(['update:from', 'update:to', 'selected:to', 'clickOutside'])

interface ChosenDates {
  from: ExtendedDate | null
  to: ExtendedDate | null
  tooltip: ExtendedDate | null
  toPreview: ExtendedDate | null
  setFrom(from: ExtendedDate | null, doEmit?: boolean): void
  setTo(to: ExtendedDate | null, doEmit?: boolean): void
  isOnlyFirstDateChosen(): this is this & { from: ExtendedDate; to: null }
  isEmpty(): boolean
  reset(tracking?: boolean): void
}

const chosenDates = reactive<ChosenDates>({
  from: null,
  to: null,
  tooltip: null,
  setFrom: function (from: ExtendedDate | null, doEmit = true) {
    this.from = from
    currentMonth.adapt(this.from, this.to)
    if (doEmit) {
      emit('update:from', from?.dmy ?? '')
    }
  },
  setTo: function (to: ExtendedDate | null, doEmit = true) {
    this.to = to
    currentMonth.adapt(this.from, this.to)
    if (doEmit) {
      emit('update:to', to?.dmy ?? '')
    }
  },
  toPreview: null,
  isOnlyFirstDateChosen: function () { return this.from && !this.to },
  isEmpty: function () { return !this.from && !this.to },
  reset: function (tracking = false) {
    this.setFrom(null)
    this.setTo(null)
    chosenDates.toPreview = null
    if (tracking) {
      const { $gtmModules } = useNuxtApp()
      $gtmModules.datepickerModule.sendDatepickerResetDateEvent()
    }
  }
} as ChosenDates)

if (import.meta.client) {
  watch(props, async (props) => {
    await nextTick()

    if (!ExtendedDate.equalsStatic(props?.from, chosenDates.from)) {
      if (props.from === null || props.from === '') {
        chosenDates.reset()
      } else {
        chosenDates.setFrom(new ExtendedDate(props.from), false)
      }
    }
    if (!ExtendedDate.equalsStatic(props?.to, chosenDates.to)) {
      if (props.to === null || props.to === '') {
        chosenDates.setTo(null)
        chosenDates.toPreview = null
      } else {
        chosenDates.setTo(new ExtendedDate(props.to), false)
      }
    }
  }, { immediate: true })
}
const { $gtmModules } = useNuxtApp()

const chooseDate = (day: ExtendedDate) => {
  chosenDates.toPreview = null

  if (chosenDates.isOnlyFirstDateChosen()) {
    if (day.isBefore(chosenDates.from)) {
      chosenDates.setFrom(day) // new start
      $gtmModules.datepickerModule.sendDatepickerArrivalDateEvent()
      return
    }
    chosenDates.setTo(day)
    $gtmModules.datepickerModule.sendDatepickerDepartureDateEvent()
    emit('selected:to', day.dmy)
  } else {
    chosenDates.reset()
    chosenDates.setFrom(day)
    chosenDates.setTo(null)
    $gtmModules.datepickerModule.sendDatepickerArrivalDateEvent()
  }
}

let tooltipTimer: ReturnType<typeof setTimeout> | null = null
const startTooltipTimer = (day: ExtendedDate) => {
  if (!('ontouchstart' in window)) {
    if (chosenDates.isOnlyFirstDateChosen() && day.isBefore(chosenDates.from)) { return }
    tooltipTimer = setTimeout(() => {
      chosenDates.tooltip = day
      isHovering.value = true
    }, 50)
  }
}

const clearTooltipTimer = () => {
  clearTimeout(tooltipTimer ?? undefined)
  chosenDates.tooltip = null
  chosenDates.toPreview = null
  isHovering.value = false
}

const isAvailableSomehow = computed(() => {
  return (day: ExtendedDate) => {
    if (chosenDates.isOnlyFirstDateChosen()) {
      return props.dateRangeCheck(chosenDates.from, day)
    } else {
      return props.dateRangeCheck(day)
    }
  }
})

defineExpose({
  chosenDates
})

</script>

<style scoped>
@reference "~/assets/css/main.css";
.day {
  @apply min-h-[30px] h-full w-full cursor-pointer font-semibold flex items-center justify-center text-sm;
  &.start-range, &.start-range.end-range{
    @apply bg-petrol-500 text-white;
    border-radius: 15px 0 0 15px !important;
  }
  &.end-range{
    @apply bg-petrol-500 text-white;
    border-radius: 0 15px 15px 0 !important;
  }
  &.in-range{
    @apply bg-petrol-50;
  }
  &.today{
    @apply border border-petrol-500 rounded-full;
  }
  &.disabled{
    pointer-events: none !important;
    &:not(.start-range){
      color: #cccccc;
    }
  }
  &.dummy{
    /* color: #cccccc */
    visibility: hidden;
  }
}

.tooltip {
  @apply absolute z-1 bg-neutral-900 text-white text-center min-w-[50px] rounded-md py-1 px-1.5 text-[12px] whitespace-nowrap;
}

.tooltip::after {
  @apply absolute w-[8px] h-[8px] rotate-45 mt-[-5px] bg-neutral-900 top-full left-[calc(50%-4px)] content-[''];
}
</style>
